import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';

const TableContainer = styled.div`
  overflow-x: auto;
  border-radius: ${props => props.theme.borderRadius?.xl || props.theme.borderRadius?.lg || '18px'};
  border: 1px solid ${props => props.theme.colors.border.light};
  background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 1) 0%,
      rgba(248, 250, 252, 0.95) 100%
    );
  box-shadow: 0 18px 48px -24px rgba(15, 23, 42, 0.25);
  transition: box-shadow 0.2s ease-in-out;

  &:hover {
    box-shadow: 0 22px 52px -18px rgba(15, 23, 42, 0.28);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${props => props.theme.typography.fontFamily.primary};
`;

const TableHeader = styled.thead`
  background: linear-gradient(
    90deg,
    ${props => props.theme.colors.background.secondary} 0%,
    ${props => props.theme.colors.background.primary} 100%
  );
`;

const TableHeaderCell = styled.th`
  padding: 1.1rem 1.25rem;
  text-align: ${props => props.align || 'left'};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.text.secondary};
  border-bottom: 1px solid ${props => props.theme.colors.border?.medium || props.theme.colors.border?.light || '#e2e8f0'};
  white-space: nowrap;
`;

const TableBody = styled.tbody`
  tr:nth-child(even) {
    background-color: rgba(248, 250, 252, 0.75);
  }
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
  transition: background-color 0.2s ease-in-out, transform 0.2s ease-in-out;
  
  &:hover {
    background-color: rgba(37, 99, 235, 0.08);
    transform: translateY(-1px);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 1.05rem 1.25rem;
  text-align: ${props => props.align || 'left'};
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.primary};
  vertical-align: middle;
`;

const EmptyState = styled.div`
  padding: 3rem 1rem;
  text-align: center;
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.fontSize.base};
`;

const LoadingState = styled.div`
  padding: 3rem 1rem;
  text-align: center;
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.fontSize.base};
`;

const TableComponent = ({
  columns = [],
  data = [],
  loading = false,
  emptyMessage = 'No data available',
  onRowClick,
  className,
  ...props
}) => {
  const { theme } = useTheme();
  
  const handleRowClick = (rowData) => {
    if (onRowClick) {
      onRowClick(rowData);
    }
  };
  
  if (loading) {
    return (
      <TableContainer theme={theme} className={className}>
        <LoadingState theme={theme}>
          Loading...
        </LoadingState>
      </TableContainer>
    );
  }
  
  if (data.length === 0) {
    return (
      <TableContainer theme={theme} className={className}>
        <EmptyState theme={theme}>
          {emptyMessage}
        </EmptyState>
      </TableContainer>
    );
  }
  
  return (
    <TableContainer theme={theme} className={className}>
      <Table theme={theme}>
        <TableHeader theme={theme}>
          <tr>
            {columns.map((column, index) => (
              <TableHeaderCell
                key={index}
                theme={theme}
                align={column.align}
                style={{ width: column.width }}
              >
                {column.header}
              </TableHeaderCell>
            ))}
          </tr>
        </TableHeader>
        <TableBody theme={theme}>
          {data.map((row, rowIndex) => (
            <TableRow
              key={rowIndex}
              theme={theme}
              onClick={() => handleRowClick(row)}
              style={{ cursor: onRowClick ? 'pointer' : 'default' }}
            >
              {columns.map((column, colIndex) => (
                <TableCell
                  key={colIndex}
                  theme={theme}
                  align={column.align}
                >
                  {column.render ? column.render(row[column.accessor], row) : row[column.accessor]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TableComponent;

